import React, { useState, useEffect } from "react";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { getStatusAppearance } from "../../../config/statusColors";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import {
  FiAlertCircle,
  FiEdit3,
  FiTrash2,
  FiTool,
  FiArchive,
  FiArrowLeft,
  FiEye,
} from "react-icons/fi";

import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const DefectiveProducts = () => {
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState({ sortBy: "defectiveAt", sortOrder: "desc" });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [statusConfirmModalOpen, setStatusConfirmModalOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deviceData, setDeviceData] = useState({
    modelId: "",
    selectedModel: null,
    serialNumber: "",
    status: "defective",
    notes: "",
  });

  const { getToken, getUser } = useAuth();
  const user = getUser();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/defective-products",
      user.pages || [],
      !!user.fullAccess,
    );

  useTitle("Rovion MES - Arızalı Ürünler");

  const getLastDefectiveLog = (device) => {
    if (!device.logs || device.logs.length === 0) return null;
    const defectiveLogs = device.logs.filter(
      (log) => log.status === "defective",
    );
    if (defectiveLogs.length === 0) return null;
    return defectiveLogs[defectiveLogs.length - 1];
  };

  const defectiveMarkedAtMs = (device) => {
    const log = getLastDefectiveLog(device);
    const raw = log ? log.changedAt : device?.updatedAt;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const modelColumns = [
    {
      name: "Model Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "tr"),
    },
    {
      name: "Model Kodu",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.code || "").localeCompare(String(b?.code || ""), "tr"),
    },
    {
      name: "Ürün Sayısı",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => (a.deviceCount || 0) - (b.deviceCount || 0),
    },
    { name: "İşlemler", id: 3 },
  ];

  const deviceColumns = [
    { name: "Model", dataType: "text", id: 1, sortable: false },
    { name: "Seri Numarası", dataType: "text", id: 0, sortKey: "serialNumber" },
    { name: "Durum", dataType: "text", id: 2, sortKey: "status" },
    {
      name: "Arızalı İşaretlenme Tarihi",
      dataType: "date",
      id: 3,
      sortKey: "defectiveAt",
      customSorter: (a, b) => defectiveMarkedAtMs(a) - defectiveMarkedAtMs(b),
    },
    { name: "Notlar", dataType: "text", id: 4, sortable: false },
    { name: translate("UI.ACTIONS"), id: 5, sortable: false },
  ];

  const {
    visibleColumns: modelVisibleColumns,
    toggleColumnVisibility: toggleModelColumn,
    resetToDefault: resetModelColumns,
  } = useColumnVisibility("defective-models-list", modelColumns);
  const {
    visibleColumns: deviceVisibleColumns,
    toggleColumnVisibility: toggleDeviceColumn,
    resetToDefault: resetDeviceColumns,
  } = useColumnVisibility("defective-devices-list", deviceColumns);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Ürünler", url: "/products" },
    { label: "Arızalı Ürünler" },
  ];

  useEffect(() => {
    fetchDeviceModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchDevices();
    }
  }, [pagination.page, pagination.limit, selectedModel, searchQuery, sort.sortBy, sort.sortOrder]);

  useEffect(() => {
    if (sidebarOpened) {
      fetchDeviceModels();
    }
  }, [sidebarOpened]);

  const fetchDevices = async () => {
    if (!selectedModel) return;

    try {
      setLoading(true);
      let url = `${API_URL}/api/devices/defective?page=${pagination.page}&limit=${pagination.limit}&modelId=${selectedModel._id}`;

      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      url += `&sortBy=${sort.sortBy}&sortOrder=${sort.sortOrder}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setDevices(response.data.devices || []);
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || pagination.page,
          limit: response.data.pagination.limit || pagination.limit,
          total: response.data.pagination.total || 0,
          totalPages: response.data.pagination.totalPages || 0,
          hasNextPage: response.data.pagination.hasNextPage || false,
          hasPrevPage: response.data.pagination.hasPrevPage || false,
        });
      }
    } catch (error) {
      console.error("Arızalı ürünler getirilemedi:", error);
      setToasts([
        {
          description:
            error.response?.data?.error ||
            "Arızalı ürünler yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceModels = async () => {
    try {
      setModelsLoading(true);
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const activeModels = response.data.filter(
        (model) => model.status === "active",
      );

      // Fetch device counts for each model
      const modelsWithCounts = await Promise.all(
        activeModels.map(async (model) => {
          try {
            const devicesResponse = await axios.get(
              `${API_URL}/api/devices/defective?modelId=${model._id}&limit=1`,
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );
            return {
              ...model,
              deviceCount: devicesResponse.data.pagination?.total || 0,
            };
          } catch (error) {
            return { ...model, deviceCount: 0 };
          }
        }),
      );

      setModels(modelsWithCounts);
    } catch (error) {
      console.error("Modeller getirilemedi:", error);
    } finally {
      setModelsLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      defective: "Arızalı",
      stock: "Stok",
      assembly: "Montaj",
      packaging: "Paketleme",
      delivered: "Teslim Edildi",
    };
    return statusLabels[status] || status;
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleEdit = (device) => {
    setEditingDevice(device);
    const selectedModel = models.find(
      (m) => m._id === device.modelId?._id || m._id === device.modelId,
    );
    const defectiveLog = getLastDefectiveLog(device);
    setDeviceData({
      modelId: device.modelId?._id || device.modelId || "",
      selectedModel: selectedModel || null,
      serialNumber: device.serialNumber || "",
      status: device.status || "defective",
      notes: defectiveLog?.notes || "",
    });
    setSidebarOpened(true);
  };

  const handleDelete = async (deviceId) => {
    setDeviceToDelete(deviceId);
    setConfirmModalOpen(true);
  };

  const handleStatusChangeClick = (device, newStatus) => {
    setPendingStatusChange({
      deviceId: device._id,
      deviceSerial: device.serialNumber,
      status: newStatus,
    });
    setStatusConfirmModalOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    try {
      await axios.put(
        `${API_URL}/api/devices/${pendingStatusChange.deviceId}`,
        { status: pendingStatusChange.status },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      const statusLabel =
        pendingStatusChange.status === "assembly" ? "Montaj" : "Paketleme";
      setToasts([
        {
          description: `Ürün ${statusLabel} durumuna alındı.`,
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      setStatusConfirmModalOpen(false);
      setPendingStatusChange(null);
      fetchDevices();
    } catch (error) {
      console.error("Durum değiştirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.error ||
            "Durum değiştirilirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      setStatusConfirmModalOpen(false);
      setPendingStatusChange(null);
    }
  };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      await axios.delete(`${API_URL}/api/devices/${deviceToDelete}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      setToasts([
        {
          description: "Ürün başarıyla silindi.",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      fetchDevices();
      setConfirmModalOpen(false);
      setDeviceToDelete(null);
    } catch (error) {
      console.error("Ürün silme hatası:", error);
      setToasts([
        {
          description: error.response?.data?.error || "Ürün silinemedi.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
      setConfirmModalOpen(false);
      setDeviceToDelete(null);
    }
  };

  const handleModelSelect = (model) => {
    setDeviceData((prev) => ({
      ...prev,
      selectedModel: model,
      modelId: model._id,
    }));
  };

  const handleUpdateSubmit = async () => {
    try {
      if (!deviceData.serialNumber || !deviceData.modelId) {
        setToasts([
          {
            description: "Seri numarası ve model zorunludur",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (deviceData.status === "defective" && !deviceData.notes?.trim()) {
        setToasts([
          {
            description: "Arıza nedeni zorunludur",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      setUpdateLoading(true);

      await axios.put(
        `${API_URL}/api/devices/${editingDevice._id}`,
        {
          serialNumber: deviceData.serialNumber,
          modelId: deviceData.modelId,
          status: deviceData.status,
          notes: deviceData.notes,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      setToasts([
        {
          description: "Ürün başarıyla güncellendi.",
          id: Date.now(),
          title: "Başarılı!",
          appearance: "success",
        },
      ]);

      closeSidebar();
      fetchDevices();
    } catch (error) {
      console.error("Ürün güncelleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.error ||
            "Ürün güncellenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setUpdateLoading(false);
    }
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingDevice(null);
    setDeviceData({
      modelId: "",
      selectedModel: null,
      serialNumber: "",
      status: "defective",
      notes: "",
    });
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBackToModels = () => {
    setSelectedModel(null);
    setDevices([]);
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <AppPage
        title={
          selectedModel
            ? `${selectedModel.name} - Arızalı Ürünler`
            : "Arızalı Ürünler"
        }
        breadcrumbs={
          selectedModel
            ? [...breadcrumbs, { label: selectedModel.name }]
            : breadcrumbs
        }
        actions={
          selectedModel ? (
            <Button
              appearance="primary-subtle"
              onClick={handleBackToModels}
              icon={<FiArrowLeft />}
            >
              Modellere Dön
            </Button>
          ) : null
        }
      >
        <Container>
          <ContainerItem sm={4} md={8} xl={12}>
            {!selectedModel ? (
              <>
                <Table
                  columns={modelColumns || []}
                  visibleColumns={modelVisibleColumns}
                  tableId="defective-models-list"
                  loading={modelsLoading}
                  search={true}
                  hideFooter={true}
                  viewOptions={
                    <ListViewOptionsPanel
                      columns={modelColumns}
                      visibleColumns={modelVisibleColumns}
                      onToggleColumn={toggleModelColumn}
                      onResetColumns={resetModelColumns}
                      showApply={false}
                      showReset={false}
                    />
                  }
                >
                  {(models || []).map((model) => (
                    <Tr key={model._id} data={model}>
                      {modelVisibleColumns.includes(0) && <Td>{model.name}</Td>}
                      {modelVisibleColumns.includes(1) && <Td>{model.code}</Td>}
                      {modelVisibleColumns.includes(2) && (
                        <Td>
                          <span
                            style={{
                              backgroundColor: "#3B82F615",
                              color: "#3B82F6",
                              padding: "5px 10px",
                              borderRadius: "5px",
                              width: "fit-content",
                              fontWeight: "600",
                            }}
                          >
                            {model.deviceCount || 0} Adet
                          </span>
                        </Td>
                      )}
                      {modelVisibleColumns.includes(3) && (
                        <Td>
                          <div className="d-flex gap-2">
                            <FiEye
                              className="cursor-pointer"
                              style={{ fontSize: "20px", cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleModelClick(model);
                              }}
                              title="Ürünleri Görüntüle"
                            />
                          </div>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Table>
              </>
            ) : (
              <>
                <Table
                  columns={deviceColumns || []}
                  visibleColumns={deviceVisibleColumns}
                  tableId="defective-devices-list"
                  loading={loading}
                  search={true}
                  externalSearch={true}
                  onSearch={(value) => {
                    setSearchQuery(value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  searchPlaceholder="Seri numarası ile ara..."
                  externalSorting={true}
                  onSort={(nextSort) => {
                    setSort(nextSort);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  hideFooter={true}
                  viewOptions={
                    <ListViewOptionsPanel
                      columns={deviceColumns}
                      visibleColumns={deviceVisibleColumns}
                      onToggleColumn={toggleDeviceColumn}
                      onResetColumns={resetDeviceColumns}
                      showApply={false}
                      showReset={false}
                    />
                  }
                >
                  {devices.map((device) => {
                    const defectiveLog = getLastDefectiveLog(device);
                    return (
                      <Tr key={device._id} data={device}>
                        {deviceVisibleColumns.includes(1) && (
                          <Td>
                            {device.modelId
                              ? `${
                                  device.modelId.name ||
                                  device.modelId.code ||
                                  "N/A"
                                }`
                              : "N/A"}
                            {device.modelId?.code && (
                              <span className="text-muted ms-1">
                                ({device.modelId.code})
                              </span>
                            )}
                          </Td>
                        )}
                        {deviceVisibleColumns.includes(0) && (
                          <Td>{device.serialNumber || "N/A"}</Td>
                        )}
                        {deviceVisibleColumns.includes(2) && (
                          <Td>
                            <div
                              style={{
                                backgroundColor: getStatusAppearance(
                                  device.status,
                                ).bg,
                                color: getStatusAppearance(device.status).text,
                                padding: "5px 10px",
                                borderRadius: "5px",
                                width: "fit-content",
                              }}
                            >
                              {getStatusLabel(device.status)}
                            </div>
                          </Td>
                        )}
                        {deviceVisibleColumns.includes(3) && (
                          <Td>
                            {defectiveLog
                              ? new Date(defectiveLog.changedAt).toLocaleString(
                                  "tr-TR",
                                )
                              : new Date(device.updatedAt).toLocaleString(
                                  "tr-TR",
                                )}
                          </Td>
                        )}
                        {deviceVisibleColumns.includes(4) && (
                          <Td>{defectiveLog?.notes || "-"}</Td>
                        )}
                        {deviceVisibleColumns.includes(5) && (
                          <Td>
                            {canWrite && (
                              <div className="d-flex gap-2">
                                <FiEdit3
                                  className="cursor-pointer text-grey"
                                  style={{ fontSize: "20px", cursor: "pointer" }}
                                  onClick={() => handleEdit(device)}
                                  title="Düzenle"
                                />
                                <FiTool
                                  className="cursor-pointer text-primary"
                                  style={{ fontSize: "20px", cursor: "pointer" }}
                                  onClick={() =>
                                    handleStatusChangeClick(device, "assembly")
                                  }
                                  title="Montaj'a Al"
                                />
                                <FiArchive
                                  className="cursor-pointer text-info"
                                  style={{ fontSize: "20px", cursor: "pointer" }}
                                  onClick={() =>
                                    handleStatusChangeClick(device, "packaging")
                                  }
                                  title="Paketleme'ye Al"
                                />
                              </div>
                            )}
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </Table>
                {!loading && pagination.total > 0 && (
                  <div className="mt-3">
                    <Pagination
                      totalItems={pagination.total}
                      totalPerPage={pagination.limit}
                      currentPage={pagination.page}
                      totalRecords={pagination.total}
                      onPageChange={({ pagination: paginationData }) => {
                        setPagination((prev) => ({
                          ...prev,
                          page: paginationData.currentPage,
                          limit: paginationData.itemsPerPage,
                        }));
                      }}
                      options={{
                        skipPrevious: true,
                        previous: true,
                        next: true,
                        skipNext: true,
                        selectQuantity: true,
                        simpleCounting: false,
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </ContainerItem>
        </Container>
      </AppPage>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeviceToDelete(null);
        }}
        width="small"
      >
        <ModalHeader>Ürünü Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu ürünü silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={confirmDelete}>
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setConfirmModalOpen(false);
                setDeviceToDelete(null);
              }}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={statusConfirmModalOpen}
        onClose={() => {
          setStatusConfirmModalOpen(false);
          setPendingStatusChange(null);
        }}
        width="small"
      >
        <ModalHeader>Durum Değiştir</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            {pendingStatusChange && (
              <>
                {pendingStatusChange.deviceSerial} seri numaralı ürünü{" "}
                <strong>
                  {pendingStatusChange.status === "assembly"
                    ? "Montaj"
                    : "Paketleme"}
                </strong>{" "}
                durumuna almak istediğinizden emin misiniz?
              </>
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance={
                pendingStatusChange?.status === "assembly"
                  ? "primary"
                  : "warning"
              }
              onClick={confirmStatusChange}
            >
              {pendingStatusChange?.status === "assembly"
                ? "Montaj'a Al"
                : "Paketleme'ye Al"}
            </Button>
            <Button
              appearance="subtle"
              onClick={() => {
                setStatusConfirmModalOpen(false);
                setPendingStatusChange(null);
              }}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <SidebarContainer
        show={sidebarOpened}
        title="Ürün Düzenle"
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="mb-4">
            <p className="text-muted small mb-3">Ürün bilgilerini düzenleyin</p>
            <div className="d-flex flex-column gap-3">
              <div>
                <Label required>Model</Label>
                <Dropdown
                  placement="top-start"
                  trigger={
                    <span>
                      {deviceData.selectedModel
                        ? `${deviceData.selectedModel.name} (${deviceData.selectedModel.code})`
                        : "Model seçin"}
                    </span>
                  }
                >
                  <DropdownContent className="w-100">
                    {models.map((model) => (
                      <DropdownItem
                        key={model._id}
                        onClick={() => handleModelSelect(model)}
                        active={model._id === deviceData.modelId}
                      >
                        {model.name} ({model.code})
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              </div>

              <div>
                <Label required>Seri Numarası</Label>
                <input
                  type="text"
                  className="form-control"
                  value={deviceData.serialNumber}
                  onChange={(e) =>
                    setDeviceData((prev) => ({
                      ...prev,
                      serialNumber: e.target.value,
                    }))
                  }
                  placeholder="Seri numarası"
                />
              </div>

              <div>
                <Label>Durum</Label>
                <Dropdown
                  placement="top-start"
                  trigger={<span>{getStatusLabel(deviceData.status)}</span>}
                >
                  <DropdownContent className="w-100">
                    {[
                      "defective",
                      "stock",
                      "assembly",
                      "packaging",
                      "delivered",
                    ].map((status) => (
                      <DropdownItem
                        key={status}
                        onClick={() =>
                          setDeviceData((prev) => ({ ...prev, status }))
                        }
                        active={status === deviceData.status}
                      >
                        {getStatusLabel(status)}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              </div>

              <div>
                <Label>
                  {deviceData.status === "defective"
                    ? "Arıza Nedeni"
                    : "Notlar"}
                  {deviceData.status === "defective" && (
                    <span style={{ color: "#dc3545", marginLeft: "4px" }}>
                      *
                    </span>
                  )}
                </Label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={deviceData.notes}
                  onChange={(e) =>
                    setDeviceData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder={
                    deviceData.status === "defective"
                      ? "Arıza nedenini girin... (Zorunlu)"
                      : "Notlar"
                  }
                  required={deviceData.status === "defective"}
                />
              </div>
            </div>
          </div>

          <div className="mt-auto pt-3 border-top">
            <ButtonGroup placement="end" reverse>
              <Button
                appearance="primary"
                onClick={handleUpdateSubmit}
                disabled={updateLoading}
              >
                {updateLoading ? "Güncelleniyor..." : "Güncelle"}
              </Button>
              <Button appearance="subtle" onClick={closeSidebar}>
                İptal
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </SidebarContainer>
    </>
  );
};

export default withAuth(DefectiveProducts);
